import { createFileRoute } from '@tanstack/react-router';
import { Navbar } from '@/components/sections/navbar';
import { SiteFooter } from '@/components/sections/site-footer';
import { Card } from '@/components/ui/card';
import { Chip } from '@/components/ui/chip';
import { ScrollReveal } from '@/components/ui/scroll-reveal';
import { useLang } from '@/lib/LanguageContext';
import { useLeadModal } from '@/lib/LeadModalContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';

/**
 * Page de capture du lead magnet (Guide AMG Québec 2026).
 *
 * Landing dédiée : promesse + ce qu'on y trouve + couverture + FAQ de désarmement.
 * Le CTA ouvre le formulaire lead global (useLeadModal) → pipeline GHL.
 * Le PDF se livre par courriel après l'opt-in (étape GHL, hors de cette page).
 */
export function GuidePage() {
  const { lang } = useLang();
  const { openForm } = useLeadModal();
  const text = ta(t.guide, lang);

  const inside = [text.inside_1, text.inside_2, text.inside_3, text.inside_4, text.inside_5];
  const faqs = [
    { q: text.faq_q1, a: text.faq_a1 },
    { q: text.faq_q2, a: text.faq_a2 },
    { q: text.faq_q3, a: text.faq_a3 },
    { q: text.faq_q4, a: text.faq_a4 },
    { q: text.faq_q5, a: text.faq_a5 }
  ];

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background text-foreground">
      <Navbar />
      <main className="flex-grow py-16 md:py-24">
        <div className="mx-auto max-w-5xl px-5 md:px-8">
          {/* Hero */}
          <div className="mb-12 text-center md:mb-16">
            <ScrollReveal
              as="span"
              className="mb-4 block font-display text-base italic tracking-wide text-secondary"
            >
              {text.eyebrow}
            </ScrollReveal>
            <h1 className="mx-auto mb-6 max-w-3xl font-display text-3xl leading-[1.15] text-balance text-foreground md:text-5xl">
              {text.title}
            </h1>
            <p className="mx-auto max-w-2xl font-sans text-base leading-relaxed text-pretty hyphens-auto text-muted-foreground">
              {text.subtitle}
            </p>
          </div>

          {/* Offre + couverture */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-start md:gap-12">
            <ScrollReveal>
              <Card className="space-y-6">
                <div>
                  <Chip tone="peach">{text.inside_label}</Chip>
                </div>
                <ul className="space-y-3">
                  {inside.map((item, i) => (
                    <li
                      key={i}
                      className="flex gap-3 font-sans text-base leading-relaxed text-pretty hyphens-auto text-foreground/85"
                    >
                      <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={openForm}
                  data-cursor="cta"
                  className="shadow-warm-soft hover:shadow-warm-md inline-flex w-full items-center justify-center rounded-md bg-primary px-6 py-4 text-center font-sans text-sm font-bold uppercase tracking-widest text-primary-foreground transition-all hover:bg-primary/90"
                >
                  {text.cta}
                </button>
                <p className="font-sans text-sm text-muted-foreground">{text.reassurance}</p>
              </Card>
            </ScrollReveal>

            <ScrollReveal delay={200} className="flex justify-center">
              <img
                src="/guides/guide-cover.png"
                alt={text.cover_alt}
                width={425}
                height={550}
                loading="eager"
                className="w-full max-w-sm rounded-lg border border-border shadow-warm-md"
              />
            </ScrollReveal>
          </div>

          {/* Contenu SEO — « assurance maladie grave » */}
          <section className="mx-auto mt-16 max-w-3xl md:mt-24">
            <ScrollReveal
              as="h2"
              className="mb-5 font-display text-2xl text-balance text-foreground md:text-3xl"
            >
              {text.seo_h2}
            </ScrollReveal>
            <p className="mb-4 font-sans text-base leading-relaxed text-pretty hyphens-auto text-muted-foreground">
              {text.seo_p1}
            </p>
            <p className="font-sans text-base leading-relaxed text-pretty hyphens-auto text-muted-foreground">
              {text.seo_p2}
            </p>
          </section>

          {/* FAQ de désarmement + SEO */}
          <section className="mx-auto mt-16 max-w-3xl md:mt-24">
            <ScrollReveal
              as="h2"
              className="mb-8 text-center font-display text-2xl text-balance text-foreground md:text-3xl"
            >
              {text.faq_title}
            </ScrollReveal>
            <div className="space-y-6">
              {faqs.map((f, i) => (
                <ScrollReveal
                  as="div"
                  key={i}
                  delay={Math.min(i * 100, 400) as 0 | 100 | 200 | 300 | 400}
                >
                  <h3 className="mb-2 font-display text-lg text-foreground">{f.q}</h3>
                  <p className="font-sans text-base leading-relaxed text-pretty hyphens-auto text-muted-foreground">
                    {f.a}
                  </p>
                </ScrollReveal>
              ))}
            </div>
          </section>

          {/* Crédibilité / AMF */}
          <p className="mx-auto mt-12 max-w-3xl text-center font-sans text-xs leading-relaxed text-pretty hyphens-auto text-muted-foreground">
            {text.credentials}
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

export const Route = createFileRoute('/guide')({
  component: GuidePage
});
